// 引入 React 副作用与引用 Hook。
import { useEffect, useRef } from 'react';
// 引入 SLS 指标与数据类型。
import { MetricTypes, SlsData } from '@/hooks/useSls';
// 引入 uid 解析与上报 uid 计算工具。
import { parseUidFromQuery, resolveReportUid } from '@/utils/userCenter';

// 登录信息接口埋点载荷结构。
export interface ILoginInfoSlsPayload {
  duration: number;
  apiPath: string;
  apiParams: string;
  uid: number | null;
  isSuccess: boolean;
  failReason: string;
  responseCode: string | number;
  stage: 'ssr' | 'post_ssr';
  scene: 'getServerSideProps' | 'app' | 'windowFocus' | 'polling';
  terminalType: 'pc' | 'm';
  viewType: 'self' | 'visitor';
}

// Hook 入参定义。
interface UseUserCenterSlsReportOptions {
  reportSlsData: (slsData: Array<SlsData>) => void;
  initialSsrDuration?: number;
  initialLoginInfoSls?: ILoginInfoSlsPayload | null;
  isSelfView: boolean;
  terminalType: 'pc' | 'm';
  accountId?: number;
  queryUid?: string | string[];
}

// 用户中心 SLS 上报 Hook（SSR 渲染耗时 + 登录信息接口耗时）。
const useUserCenterSlsReport = ({
  reportSlsData,
  initialSsrDuration = 0,
  initialLoginInfoSls = null,
  isSelfView,
  terminalType,
  accountId,
  queryUid,
}: UseUserCenterSlsReportOptions) => {
  // 标记 SSR 耗时埋点是否已上报。
  const ssrSlsReportedRef = useRef(false);
  // 标记登录信息接口埋点是否已上报。
  const loginInfoSlsReportedRef = useRef(false);

  // 上报 SSR 渲染耗时埋点。
  useEffect(() => {
    // 已上报过则跳过。
    if (ssrSlsReportedRef.current) return;
    // SSR 环境跳过。
    if (typeof window === 'undefined') return;

    // 归一化耗时。
    const duration = Number(initialSsrDuration);
    // 非法耗时直接跳过。
    if (!Number.isFinite(duration) || duration < 0) return;

    // 解析 query uid 并计算上报 uid。
    const parsedUid = parseUidFromQuery(queryUid);
    const reportUid = resolveReportUid(parsedUid, accountId);

    // 上报 SSR 耗时指标与环境信息。
    reportSlsData([
      ['reportMetric', MetricTypes.userCenterSsrRenderTime, duration],
      [
        'setEnvironment',
        {
          user_center_view_type: isSelfView ? 'self' : 'visitor',
          terminal_type: terminalType,
          ssr_duration: duration,
          page_route: window.location.pathname,
          route_params: window.location.search || '',
          uid: reportUid,
        },
      ],
    ]);
    // 标记已上报。
    ssrSlsReportedRef.current = true;
  }, [accountId, initialSsrDuration, isSelfView, queryUid, reportSlsData, terminalType]);

  // 上报登录信息接口埋点（SSR 预取结果）。
  useEffect(() => {
    // 已上报过则跳过。
    if (loginInfoSlsReportedRef.current) return;
    // SSR 环境跳过。
    if (typeof window === 'undefined') return;
    // 无初始载荷跳过。
    if (!initialLoginInfoSls) return;
    // 非法耗时跳过。
    if (!Number.isFinite(Number(initialLoginInfoSls.duration))) return;

    // 上报登录信息接口耗时指标与环境信息。
    reportSlsData([
      ['reportMetric', MetricTypes.userCenterLoginInfoApiTime, Number(initialLoginInfoSls.duration)],
      [
        'setEnvironment',
        {
          api_path: initialLoginInfoSls.apiPath,
          api_params: initialLoginInfoSls.apiParams,
          uid: initialLoginInfoSls.uid,
          is_success: initialLoginInfoSls.isSuccess ? 1 : 0,
          fail_reason: initialLoginInfoSls.failReason,
          response_code: initialLoginInfoSls.responseCode,
          terminal_type: initialLoginInfoSls.terminalType,
          stage: initialLoginInfoSls.stage,
          scene: initialLoginInfoSls.scene,
          user_center_view_type: initialLoginInfoSls.viewType,
        },
      ],
    ]);
    // 标记已上报。
    loginInfoSlsReportedRef.current = true;
  }, [initialLoginInfoSls, reportSlsData]);
};

// 导出 Hook。
export default useUserCenterSlsReport;
